import { Lexer, type Token, type Tokens } from 'marked';
import { isSafeMarkdownUrl } from '../../lib/markdown-links';

const escapeHTML = (text: string) => text.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);

/** Only generated, escaped markup enters the editor; pasted HTML never does. */
export function noteToHTML(source: string): string {
  const render = (tokens: readonly Token[]): string => tokens.map(token => {
    const children = () => render('tokens' in token && Array.isArray(token.tokens) ? token.tokens : []);
    switch (token.type) {
      case 'space': return '';
      case 'paragraph': return `<p>${children() || '<br>'}</p>`;
      case 'heading': return `<h${token.depth}>${children()}</h${token.depth}>`;
      case 'text': return token.tokens ? children() : escapeHTML(token.text);
      case 'escape': return escapeHTML(token.text);
      case 'strong': return `<strong>${children()}</strong>`;
      case 'em': return `<em>${children()}</em>`;
      case 'del': return `<s>${children()}</s>`;
      case 'br': return '<br>';
      case 'html': return /^<br\s*\/?\s*>$/i.test(token.raw.trim()) ? '<br>'
        : `<span contenteditable="false" data-note-source="${escapeHTML(token.raw)}">${escapeHTML(token.raw)}</span>`;
      case 'codespan': return `<code>${escapeHTML(token.text)}</code>`;
      case 'blockquote': return `<blockquote>${children()}</blockquote>`;
      case 'hr': return '<hr>';
      case 'link': return isSafeMarkdownUrl(token.href)
        ? `<a href="${escapeHTML(token.href)}"${token.title ? ` title="${escapeHTML(token.title)}"` : ''}>${children()}</a>` : children();
      case 'code': return `<pre data-language="${escapeHTML(token.lang ?? '')}"><code>${escapeHTML(token.text)}</code></pre>`;
      case 'list': {
        const list = token as Tokens.List;
        const tag = list.ordered ? 'ol' : 'ul';
        return `<${tag}${list.ordered ? ` start="${Number(list.start) || 1}"` : ''}>${list.items.map(item =>
          `<li>${item.task ? `<input type="checkbox" contenteditable="false" aria-label="Checklist item"${item.checked ? ' checked' : ''}>` : ''}${render(item.tokens)}</li>`).join('')}</${tag}>`;
      }
      case 'table': {
        const table = token as Tokens.Table;
        const row = (cells: Tokens.TableCell[], tag: string) => `<tr>${cells.map((cell, i) => `<${tag}${table.align[i] ? ` align="${table.align[i]}"` : ''}>${render(cell.tokens)}</${tag}>`).join('')}</tr>`;
        return `<table><thead>${row(table.header, 'th')}</thead><tbody>${table.rows.map(cells => row(cells, 'td')).join('')}</tbody></table>`;
      }
      // Preserve images, raw HTML and definitions verbatim without executing or fetching them.
      default: return `<span contenteditable="false" data-note-source="${escapeHTML(token.raw)}">${escapeHTML(token.raw)}</span>`;
    }
  }).join('');
  return render(Lexer.lex(source, { gfm: true })) || '<p><br></p>';
}

const escapeText = (text: string) => text.replace(/([\\`*_[\]<>~])/g, '\\$1')
  .replace(/^(\s*)([#+>\-]|\d+[.)])(?=\s)/gm, '$1\\$2');
const fenceFor = (text: string, minimum: number) => '`'.repeat(Math.max(minimum, ...[...text.matchAll(/`+/g)].map(match => match[0].length + 1)));
const wrapInline = (text: string, marker: string) => text.replace(/^(\s*)([\s\S]*?)(\s*)$/, (_match, leading: string, content: string, trailing: string) =>
  content ? `${leading}${marker}${content}${marker}${trailing}` : text);

/** Convert the browser's editable document back to the existing note body format. */
export function noteFromDOM(root: HTMLElement): string {
  const children = (node: Node): string => [...node.childNodes].map(serialize).join('');
  const serialize = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return escapeText(node.textContent ?? '');
    if (!(node instanceof HTMLElement)) return '';
    if (node.dataset.noteSource !== undefined) return node.dataset.noteSource;
    const text = () => children(node);
    const inline = () => text().replace(/\n{2,}/g, '\n').trim();
    switch (node.tagName) {
      case 'BR': return '\n';
      case 'INPUT': return '';
      case 'B': case 'STRONG': return wrapInline(text(), '**');
      case 'I': case 'EM': return wrapInline(text(), '*');
      case 'S': case 'STRIKE': case 'DEL': return wrapInline(text(), '~~');
      case 'CODE': {
        const raw = node.textContent ?? '';
        const fence = fenceFor(raw, 1);
        const pad = raw.startsWith('`') || raw.endsWith('`') || /^ .* $/.test(raw) ? ' ' : '';
        return `${fence}${pad}${raw}${pad}${fence}`;
      }
      case 'PRE': {
        const raw = node.textContent ?? '';
        const fence = fenceFor(raw, 3);
        return `${fence}${(node.dataset.language ?? '').replace(/[\r\n`]/g, '')}\n${raw}\n${fence}\n\n`;
      }
      case 'A': {
        const url = node.getAttribute('href') ?? '';
        const title = node.getAttribute('title');
        return isSafeMarkdownUrl(url) ? `[${text()}](<${url.replace(/>/g, '%3E')}>${title ? ` "${title.replace(/[\\"]/g, '\\$&')}"` : ''})` : text();
      }
      case 'H1': case 'H2': case 'H3': case 'H4': case 'H5': case 'H6':
        return `${'#'.repeat(Number(node.tagName[1]))} ${inline()}\n\n`;
      case 'P': case 'DIV': return `${text().replace(/\n$/, '')}\n\n`;
      case 'BLOCKQUOTE': return `${text().trim().split('\n').map(line => `> ${line}`).join('\n')}\n\n`;
      case 'HR': return '---\n\n';
      case 'UL': case 'OL': {
        const start = Number(node.getAttribute('start')) || 1;
        return '\n' + [...node.children].filter(item => item.tagName === 'LI').map((item, index) => {
          const checkbox = [...item.children].find(child => child instanceof HTMLInputElement) as HTMLInputElement | undefined;
          const prefix = node.tagName === 'OL' ? `${start + index}. ` : '- ';
          const task = checkbox ? `[${checkbox.checked ? 'x' : ' '}] ` : '';
          const lines = children(item).trim().split('\n');
          return `${prefix}${task}${lines[0] ?? ''}${lines.slice(1).map(line => `\n${' '.repeat(prefix.length)}${line}`).join('')}`;
        }).join('\n') + '\n\n';
      }
      case 'TABLE': {
        const rows = [...node.querySelectorAll('tr')];
        const first = rows[0];
        if (!first) return '';
        const row = (element: Element) => `| ${[...element.children].map(cell => children(cell).trim().replace(/\|/g, '\\|').replace(/\n/g, '<br>')).join(' | ')} |`;
        const separators = [...first.children].map(cell => {
          const align = cell.getAttribute('align');
          return align === 'center' ? ':---:' : align === 'right' ? '---:' : align === 'left' ? ':---' : '---';
        });
        return `${row(first)}\n| ${separators.join(' | ')} |\n${rows.slice(1).map(row).join('\n')}\n\n`;
      }
      default: return text();
    }
  };
  return children(root).replace(/\u00a0/g, ' ').trim();
}
